import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { readCityChoice, rememberCity } from "../lib/city";
import { RTL } from "../i18n";

const StoreContext = createContext(null);
export const useStore = () => useContext(StoreContext);

const read = (k, fallback) => {
  try {
    const v = localStorage.getItem(k);
    return v ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
};

const migrate = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (raw.gifts) return raw.gifts;
  return [{ ...raw, gift_id: "g1" }];
};

export const StoreProvider = ({ locale, children }) => {
  const [currency, setCurrency] = useState(() => read("fl_currency", "EUR"));
  const [boot, setBoot] = useState(null);
  const [gifts, setGifts] = useState(() => migrate(read("fl_cart", null)));
  const giftsRef = useRef(gifts);
  const [account, setAccount] = useState(null);
  const [promoCode, setPromoCode] = useState(() => read("fl_promo", ""));
  // The delivery city the buyer chose. Everything city-flavoured on the storefront follows it.
  const [cityChoice, setCityChoiceState] = useState(() => readCityChoice());
  const setCityChoice = (city) => {
    rememberCity(city);
    setCityChoiceState(readCityChoice());
  };

  useEffect(() => {
    const fromUrl = new URLSearchParams(window.location.search).get("code");
    if (fromUrl) {
      const code = fromUrl.toUpperCase();
      setPromoCode(code);
      localStorage.setItem("fl_promo", JSON.stringify(code));
    }
  }, []);

  useEffect(() => {
    api.get("/bootstrap", { params: { locale, currency } }).then((r) => setBoot(r.data)).catch(() => {});
  }, [locale, currency]);

  useEffect(() => {
    localStorage.setItem("fl_currency", JSON.stringify(currency));
  }, [currency]);

  const refreshAccount = () => {
    if (!localStorage.getItem("fl_token")) {
      setAccount(false);
      return Promise.resolve(false);
    }
    return api
      .get("/auth/me")
      .then((r) => {
        const isBuyer = r.data.user?.role === "buyer";
        setAccount(isBuyer ? r.data.user : false);
        return isBuyer ? r.data.user : false;
      })
      .catch(() => {
        setAccount(false);
        return false;
      });
  };

  useEffect(() => {
    refreshAccount();
    document.documentElement.lang = locale;
    document.documentElement.dir = RTL.includes(locale) ? "rtl" : "ltr";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  const persist = (next) => {
    giftsRef.current = next;
    setGifts(next);
    try { localStorage.setItem("fl_cart", JSON.stringify(next)); } catch (_) { /* Keep the current basket usable when storage is unavailable. */ }
  };

  const newId = () => `g${Date.now()}${Math.random().toString(36).slice(2, 6)}`;
  const addGift = (gift) => persist([...giftsRef.current, { ...gift, gift_id: newId() }]);
  // Adds several lines in one write — used by the Meta Commerce checkout URL.
  const addGifts = (list) => persist([...giftsRef.current, ...list.map((g) => ({ ...g, gift_id: newId() }))]);
  const removeGift = (giftId) => persist(giftsRef.current.filter((g) => g.gift_id !== giftId));
  const updateGift = (giftId, patch) => persist(giftsRef.current.map((g) => (g.gift_id === giftId ? { ...g, ...patch } : g)));
  const clearCart = () => persist([]);

  const signOut = async () => {
    localStorage.removeItem("fl_token");
    setAccount(false);
  };

  const value = useMemo(
    () => {
      const cities = boot?.cities || [];
      const activeCity = cities.find((c) => c.key === cityChoice?.key || c.slug === cityChoice?.slug) || cities[0];
      return { locale, currency, setCurrency, boot, gifts, addGift, addGifts, removeGift, updateGift, clearCart,
               account, refreshAccount, signOut, promoCode, cityChoice, setCityChoice, activeCity,
               rtl: RTL.includes(locale) };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locale, currency, boot, gifts, account, promoCode, cityChoice]
  );

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
};
